import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';

import { Observable, Subject, fromEvent, merge, race, switchMap, takeUntil, timer } from 'rxjs';

import { fadeOnAddRemove } from '@hdms/shared/util-core';

import { Direction } from './button-slider-component.abstract';
import { ButtonSliderService } from './button-slider.service';

const NAMESPACE = 'shrd-uic-button-slider-arrow';

@Component({
  selector: 'shrd-uic-button-slider-arrow',
  template: '<ng-content></ng-content>',
  styleUrls: ['./button-slider-arrow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeOnAddRemove('fade', 0.25)]
})
export class ButtonSliderArrowComponent implements OnInit, OnDestroy {
  private slideDirection: Direction;
  private readonly unsubscribe$ = new Subject<void>();

  @HostBinding('@fade') private readonly fade = true;

  @HostBinding('@.disabled')
  @Input()
  disableFade = false;

  @Input()
  set direction(direction: Direction) {
    this.slideDirection = direction;
    this.renderer.addClass(this.element.nativeElement, `${NAMESPACE}-${direction}`);
  }

  constructor(
    private readonly element: ElementRef<HTMLElement>,
    private readonly buttonSliderService: ButtonSliderService,
    private readonly renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.getMouseEvents$().subscribe(() => {
      this.buttonSliderService.changeDirection(this.slideDirection);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getMouseEvents$(): Observable<number | Event> {
    const domItem: HTMLElement = this.element.nativeElement;
    const click$ = fromEvent(domItem, 'click');
    const start$ = merge(fromEvent(domItem, 'mousedown'), fromEvent(domItem, 'touchstart', { passive: true }));
    const end$ = merge(fromEvent(domItem, 'mouseup'), fromEvent(domItem, 'touchend', { passive: true }));
    const mouseLeave$ = fromEvent(domItem, 'mouseleave');

    return race(click$, start$.pipe(switchMap(() => timer(15, 250).pipe(takeUntil(merge(end$, mouseLeave$)))))).pipe(
      takeUntil(this.unsubscribe$)
    );
  }
}
